/**
 * One row per task type the agency can create: its kind, discipline and
 * aliases, whether it bills, and whether a new patient gets one.
 */

import React, { useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Globe, Minus } from 'lucide-react';
import { TooltipProvider } from '@roger/ui-components';
import {
  BoolCell,
  OpenCell,
  ReadCell,
  Spreadsheet,
  useFillHeight,
} from '../../../components/spreadsheet';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { matchesNameOrAlias } from '../../../components/admin/matchesNameOrAlias';
import { getDisciplineColor } from '../../../utils/worksheetGoals';
import { AliasDialog } from './AliasDialog';
import type { Resolved } from './clinicScope';
import { kindOf } from './kinds';
import { DiffersTip, Tip } from './tips';
import type { VisitTypesData, VisitTypesRow } from './useVisitTypesData';

/** One neutral outline, so the Discipline badges are the only colour. */
const KIND_STYLE = 'border-slate-300 bg-white text-slate-600';

/** Unsaved edits are tinted. */
const PENDING_CLASS = 'bg-[color-mix(in_srgb,hsl(var(--primary))_8%,white)]';
const pend = (on: boolean, node: React.ReactNode) =>
  on ? <div className={PENDING_CLASS}>{node}</div> : node;

const AgencyWideHeader: React.FC<{ label: string }> = ({ label }) => (
  <Tip tip='Agency-wide: the same for every clinic'>
    <span className='inline-flex items-center gap-1'>
      {label}
      <Globe className='h-3 w-3 text-muted-foreground' />
    </span>
  </Tip>
);

const aliasNames = (row: VisitTypesRow) =>
  (row.taskTypeRow?.aliases ?? []).map(alias => alias.name);

/**
 * The non-billable OASIS types record an assessment without a claim line, so
 * they cannot be made billable here.
 */
const isLockedNonBillable = (row: VisitTypesRow) =>
  !!row.taskTypeRow?.roger_default_non_billable &&
  row.taskTypeRow.visit_type.startsWith('OASIS_');

/** Every new patient gets one, whatever a clinic lists. */
const ALWAYS_CREATED = new Set(['misc:Admission Consents']);

/**
 * An OASIS assessment comes from the intake, so it is never listed. The
 * backend's answer when the row has one; a guess from the kind otherwise.
 */
const isAssessment = (row: VisitTypesRow) =>
  row.taskTypeRow
    ? row.taskTypeRow.auto_creatable === false
    : row.category === 'oasis' || row.type.startsWith('OASIS_');

/** A box the admin cannot change here, with the reason on hover. */
const LockedCell: React.FC<{
  reason: string;
  label: string;
  value?: boolean;
}> = ({ reason, label, value = false }) => (
  <Tip tip={reason}>
    <div>
      <BoolCell value={value} disabled label={label} onCommit={() => {}} />
    </div>
  </Tip>
);

/** Aliases that do not apply: left blank, so "—" keeps meaning "none yet". */
const NoAliases: React.FC = () => (
  <Tip tip='Only visit types have aliases.'>
    <div>
      <ReadCell muted>{''}</ReadCell>
    </div>
  </Tip>
);

/** Clinics disagree: a dash box; a click turns it on for every clinic. */
const MixedBoolCell: React.FC<{
  tip: React.ReactNode;
  label: string;
  disabled: boolean;
  onCommit: (value: boolean) => void;
}> = ({ tip, label, disabled, onCommit }) => (
  <Tip tip={tip}>
    <div className='flex h-9 w-full items-center justify-center hover:bg-muted/60'>
      <button
        type='button'
        role='checkbox'
        aria-checked='mixed'
        aria-label={label}
        disabled={disabled}
        className='flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50'
        onClick={() => onCommit(true)}
      >
        <Minus className='h-3 w-3' />
      </button>
    </div>
  </Tip>
);

export const VisitTypesGrid: React.FC<{
  data: VisitTypesData;
  search: string;
  kindFilter: string[];
  onKindFilterChange: (next: string[]) => void;
  canEditVisitTypes: boolean;
  canEditAutoCreate: boolean;
  hiddenColumnIds: string[];
}> = ({
  data,
  search,
  kindFilter,
  onKindFilterChange,
  canEditVisitTypes,
  canEditAutoCreate,
  hiddenColumnIds,
}) => {
  const {
    rows,
    billableFor,
    saveBillable,
    autoCreateFor,
    setAutoCreated,
    isPending,
    saving,
  } = data;
  const [aliasRow, setAliasRow] = useState<VisitTypesRow | null>(null);
  // On a phone the pinned name column is narrower, so a setting fits beside it.
  const isMobile = useIsMobile();
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Ends above the messages drawer, as the User Manager grid does.
  const maxHeight = useFillHeight(wrapperRef, { bottomInset: 72, min: 240 });

  const kindOptions = useMemo(() => {
    const seen = new Map<string, { label: string; order: number }>();
    for (const row of rows) seen.set(row.category, kindOf(row));
    return [...seen.entries()]
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([value, { label }]) => ({ value, label }));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(
      row =>
        kindFilter.includes(row.category) &&
        (!query || matchesNameOrAlias(query, row.name, aliasNames(row)))
    );
  }, [rows, search, kindFilter]);

  const columns = useMemo<ColumnDef<VisitTypesRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Task type',
        accessorFn: r => r.name,
        size: isMobile ? 150 : 260,
        meta: { clipboard: { copy: r => r.name } },
        cell: ({ row }) => (
          <ReadCell title={row.original.name}>{row.original.name}</ReadCell>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        accessorFn: r => kindOf(r).order,
        size: 90,
        meta: { headerAlign: 'center' },
        cell: ({ row }) => (
          <div className='flex h-9 items-center justify-center overflow-hidden px-1.5'>
            <span
              title={kindOf(row.original).label}
              className={`max-w-full truncate whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${KIND_STYLE}`}
            >
              {kindOf(row.original).label}
            </span>
          </div>
        ),
      },
      {
        id: 'discipline',
        header: 'Discipline',
        accessorFn: r => r.discipline ?? '',
        size: 90,
        meta: { headerAlign: 'center' },
        cell: ({ row }) => {
          const discipline = row.original.discipline;
          return (
            <div className='flex h-9 items-center justify-center px-2'>
              {discipline ? (
                <span
                  className={`whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${getDisciplineColor(discipline)}`}
                >
                  {discipline}
                </span>
              ) : (
                <span className='text-[13px] text-muted-foreground'>—</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'aliases',
        header: () => <AgencyWideHeader label='Aliases' />,
        accessorFn: r => aliasNames(r).length,
        size: 180,
        meta: { clipboard: { copy: r => aliasNames(r).join(', ') } },
        cell: ({ row }) => {
          if (!row.original.taskTypeRow) return <NoAliases />;
          const names = aliasNames(row.original);
          const cell = (
            <div>
              <OpenCell
                text={
                  names.length === 0
                    ? '—'
                    : names.length === 1
                      ? names[0]
                      : `${names.length} aliases`
                }
                muted={names.length !== 1}
                onOpen={() => setAliasRow(row.original)}
              />
            </div>
          );
          // The names behind a count, without opening the editor.
          return names.length > 1 ? (
            <Tip
              tip={
                <ul className='space-y-0.5'>
                  {names.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              }
            >
              {cell}
            </Tip>
          ) : (
            cell
          );
        },
      },
      {
        id: 'billable',
        header: () => <AgencyWideHeader label='Billable' />,
        accessorFn: r => {
          const billable = billableFor(r);
          return billable === null ? -1 : Number(billable);
        },
        size: 90,
        meta: { headerAlign: 'center' },
        cell: ({ row }) => {
          const billable = billableFor(row.original);
          if (billable === null)
            return (
              <LockedCell
                reason='Only visit types bill.'
                label={`${row.original.name} billable`}
              />
            );
          const label = `${row.original.name} billable`;
          // Can be turned off, never on: an agency that bills one already can
          // put it back.
          if (isLockedNonBillable(row.original) && !billable) {
            return (
              <LockedCell
                reason='This OASIS type exists to record a discharge without billing it, so it stays non-billable.'
                label={label}
              />
            );
          }
          return pend(
            isPending(row.original, 'billable'),
            <BoolCell
              value={billable}
              disabled={!canEditVisitTypes || saving}
              label={label}
              onCommit={value => saveBillable(row.original, value)}
            />
          );
        },
      },
      {
        id: 'auto_create',
        header: 'Create on admission',
        accessorFn: r => {
          const auto = autoCreateFor(r);
          return auto.kind === 'value' ? Number(auto.value) : 0.5;
        },
        size: 100,
        meta: { headerAlign: 'center' },
        cell: ({ row }) => {
          if (isAssessment(row.original))
            return (
              <LockedCell
                reason='The OASIS assessment comes from the intake, so it is not listed here.'
                label={`Create ${row.original.name} for a new patient`}
              />
            );
          if (ALWAYS_CREATED.has(row.original.id)) {
            return (
              <LockedCell
                reason='Every new patient gets this, whatever the clinic lists.'
                label={`${row.original.name} is always created`}
                value
              />
            );
          }
          const auto: Resolved<boolean> = autoCreateFor(row.original);
          const label = `Create ${row.original.name} for a new patient`;
          const commit = (value: boolean) =>
            setAutoCreated(row.original, value);
          const pending = isPending(row.original, 'auto');
          if (auto.kind === 'mixed') {
            return pend(
              pending,
              <MixedBoolCell
                tip={
                  <DiffersTip
                    byClinic={auto.byClinic}
                    show={v => (v ? 'On' : 'Off')}
                  />
                }
                label={label}
                disabled={!canEditAutoCreate || saving}
                onCommit={commit}
              />
            );
          }
          return pend(
            pending,
            <BoolCell
              value={auto.kind === 'value' && auto.value}
              disabled={!canEditAutoCreate || saving}
              label={label}
              onCommit={commit}
            />
          );
        },
      },
    ],
    [
      billableFor,
      saveBillable,
      autoCreateFor,
      setAutoCreated,
      isPending,
      canEditVisitTypes,
      canEditAutoCreate,
      saving,
      isMobile,
    ]
  );

  return (
    <TooltipProvider delayDuration={150}>
      {/* An unticked box that can't be changed gets a grey fill, so it reads
          apart from one that can. */}
      <div
        ref={wrapperRef}
        className='[&_button[role=checkbox][data-state=unchecked]:disabled]:bg-muted'
      >
        <Spreadsheet
          data={visibleRows}
          columns={columns}
          getRowId={r => r.id}
          initialSorting={[
            { id: 'kind', desc: false },
            { id: 'name', desc: false },
          ]}
          pinLeft={1}
          hiddenColumnIds={hiddenColumnIds}
          columnFilters={{
            kind: {
              options: kindOptions,
              selected: kindFilter,
              onChange: onKindFilterChange,
            },
          }}
          maxHeight={maxHeight}
          emptyText='No task types match.'
        />
      </div>
      <AliasDialog
        row={aliasRow}
        data={data}
        canEdit={canEditVisitTypes}
        onClose={() => setAliasRow(null)}
      />
    </TooltipProvider>
  );
};
